import { useCallback, useEffect, useState } from "react";
import { ForkKnife, Coffee, Truck, Storefront, Plus, Minus, Broadcast, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState } from "@/components/Shared";
import { ShareBar } from "@/components/ShareBar";
import { Vending } from "@/components/Vending";
import { GroceryRun } from "@/components/GroceryRun";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));
const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const KIND_ICON = { food_truck: Truck, vending: Storefront, canteen: ForkKnife };
const STATUS_TONE = {
  placed: "border-[#002FA7] bg-[#EEF3FF] text-[#002FA7]",
  preparing: "border-[#B3241B] bg-[#FFF6E5] text-[#8E1C15]",
  ready: "border-[#0F7A3D] bg-[#EAF7EF] text-[#0B5C2E]",
  collected: "border-border bg-slate-100 text-slate-500",
  cancelled: "border-border bg-slate-100 text-slate-400",
};

export default function Cafeteria() {
  const { user } = useAuth();
  const [view, setView] = useState("order");
  const [data, setData] = useState(null);
  const [outletId, setOutletId] = useState(null);
  const [cart, setCart] = useState({});
  const [pay, setPay] = useState("counter");
  const [slot, setSlot] = useState("");
  const [crew, setCrew] = useState({ on: false, heads: 1, note: "", company: false });
  const [orders, setOrders] = useState([]);
  const [board, setBoard] = useState(null);
  const [credit, setCredit] = useState(null);
  const [ask, setAsk] = useState("");
  const [ai, setAi] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/cafeteria/outlets").then(({ data: d }) => {
      setData(d);
      setOutletId((cur) => cur || d.outlets[0]?.id || null);
    }).catch(oops);
  }, []);

  const loadOrders = useCallback(() => {
    api.get("/cafeteria/orders").then(({ data: d }) => setOrders(d)).catch(() => {});
  }, []);
  const loadBoard = useCallback(() => {
    api.get("/cafeteria/board").then(({ data: d }) => setBoard(d)).catch(() => {});
  }, []);

  useEffect(() => { load(); loadOrders(); loadBoard(); }, [load, loadOrders, loadBoard]);
  useEffect(() => {
    const id = setInterval(loadBoard, 20000);
    return () => clearInterval(id);
  }, [loadBoard]);
  useEffect(() => {
    if (view === "credit") api.get("/cafeteria/credit").then(({ data: d }) => setCredit(d)).catch(oops);
  }, [view]);

  const outlet = data?.outlets.find((o) => o.id === outletId);
  const lines = Object.entries(cart).filter(([, q]) => q > 0);
  const items = outlet?.items || [];
  const total = lines.reduce((sum, [id, q]) => {
    const item = items.find((i) => i.id === id);
    return sum + (item ? item.price * q : 0);
  }, 0);

  const bump = (id, by) => setCart((c) => ({ ...c, [id]: Math.max((c[id] || 0) + by, 0) }));

  const placeOrder = async () => {
    setBusy(true);
    try {
      const { data: order } = await api.post("/cafeteria/orders", {
        outlet_id: outletId,
        lines: lines.map(([item_id, quantity]) => ({ item_id, quantity })),
        pay_method: pay, paid_by: crew.company ? "company" : "self",
        for_crew: crew.on, heads: Number(crew.heads) || 1,
        crew_note: crew.note || null, slot: slot || null,
      });
      setCart({});
      toast.success(`${order.reference} placed — ${order.currency} ${order.total}`);
      loadOrders(); loadBoard();
      if (pay === "stripe") {
        const { data: session } = await api.post("/payments/cafeteria/checkout", {
          order_id: order.id, origin_url: window.location.origin });
        window.location.href = session.checkout_url;
      }
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const askAi = async (place = false) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/cafeteria/ai-order", {
        request: ask, place, pay_method: pay, slot: slot || null });
      setAi(d);
      if (place) {
        if (d.failed?.length) {
          toast.error(`${d.failed[0].outlet_name}: ${d.failed[0].reason}`);
        }
        if (d.placed.length) toast.success(`${d.placed.length} order(s) placed`);
        setAi(null); setAsk("");
        loadOrders(); loadBoard();
      } else if (d.drafts.length === 0) {
        toast.error(d.note || "Nothing on the menus matched that");
      }
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const move = async (id, status) => {
    try {
      await api.post(`/cafeteria/orders/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      loadBoard(); loadOrders();
    } catch (e) { oops(e); }
  };

  const fillPics = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/cafeteria/pictures/fill-all");
      toast.success(`${d.pictures} drawn · ${d.left} still without a photo`);
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const makePoster = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/cafeteria/poster");
      toast.success(`Poster drawn for ${d.day}`);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const postSpecial = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/cafeteria/special/post");
      toast.success(d.posted ? "Today's special is on the Live Feed — ready to share" : d.reason);
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const refreshPics = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/cafeteria/pictures/refresh");
      toast.success(`${d.pictures} fresh food picture(s)`);
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Loading the cafeteria…</p>;

  return (
    <div className="space-y-5" data-testid="cafeteria-page">
      <PageHeader overline="On site" title="Cafeteria" testid="cafeteria-header"
        description="Order from the outlets on site, the local stall, our own counter, the food truck and the machines — for yourself or for the crew." />

      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="cafeteria-credit-badge"
          className="inline-flex items-center gap-1.5 border border-[#0F7A3D] bg-[#EAF7EF] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#0B5C2E]">
          <Coffee size={13} /> Meal credit {data.credit_currency} {data.credit_balance.toFixed(2)}
        </span>
        {data.can_manage && (
          <>
            <Button variant="outline" data-testid="cafeteria-refresh-pictures" onClick={refreshPics} disabled={busy}>
              <Sparkle size={15} className="mr-1.5" /> {busy ? "Drawing…" : "Fresh food pictures"}
            </Button>
            <Button variant="outline" data-testid="cafeteria-fill-pictures" onClick={fillPics} disabled={busy}>
              Fill missing photos
            </Button>
            <Button variant="outline" data-testid="cafeteria-make-poster" onClick={makePoster} disabled={busy}>
              Today's poster
            </Button>
            <Button variant="outline" data-testid="cafeteria-post-special" onClick={postSpecial} disabled={busy}>
              Post today's special
            </Button>
          </>
        )}
        <a href="/food-court" target="_blank" rel="noreferrer" data-testid="cafeteria-public-link"
          className="tap-safe border border-border px-3 py-2 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
          Public food court board
        </a>
        <ShareBar url={`${BACKEND}/api/share/food-court`} text="What's on the food court today"
          testid="cafeteria-board-share" compact instagram />
      </div>

      {data.specials.length > 0 && (
        <div className="border-l-2 border-[#B3241B] bg-[#FFF6E5] p-4" data-testid="cafeteria-specials">
          <p className="overline text-[#8E1C15]">Today's specials</p>
          <div className="scroll-x mt-3 flex gap-3 overflow-x-auto">
            {data.specials.map((s, i) => (
              <div key={s.id} data-testid={`cafeteria-special-${i}`} className="w-44 shrink-0 border border-border bg-white">
                {s.image_url && <img src={`${BACKEND}${s.image_url}`} alt={s.name} className="h-28 w-full object-cover" />}
                <div className="p-2.5">
                  <p className="truncate text-sm font-semibold text-[#0F172A]">{s.name}</p>
                  <p className="text-xs text-slate-500">{s.symbol}{s.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs value={view} onValueChange={setView}>
        <TabsList className="scroll-x w-full justify-start overflow-x-auto" data-testid="cafeteria-tabs">
          <TabsTrigger value="order" data-testid="cafeteria-tab-order">Order food</TabsTrigger>
          <TabsTrigger value="mine" data-testid="cafeteria-tab-mine">My orders</TabsTrigger>
          <TabsTrigger value="board" data-testid="cafeteria-tab-board">Counter board</TabsTrigger>
          <TabsTrigger value="vending" data-testid="cafeteria-tab-vending">Vending</TabsTrigger>
          {(user?.role === "admin" || user?.role === "manager") && (
            <TabsTrigger value="grocery" data-testid="cafeteria-tab-grocery">Grocery run</TabsTrigger>
          )}
          <TabsTrigger value="credit" data-testid="cafeteria-tab-credit">Credit</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "order" && (
        <>
          <div className="border border-border bg-white p-4" data-testid="cafeteria-ai-order">
            <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F172A]">
              <Sparkle size={15} className="text-[#002FA7]" /> Say what you fancy
            </p>
            <p className="mt-1 text-xs text-slate-600">
              Plain words — "two chicken tacos, a black coffee and something veg for four of us" —
              and it picks it off the menus across every outlet.
            </p>
            <div className="mt-3 flex flex-wrap items-end gap-2">
              <Input data-testid="cafeteria-ai-input" className="w-full sm:w-96" value={ask}
                placeholder="Coffee and a halal wrap for three" onChange={(e) => setAsk(e.target.value)} />
              <Button data-testid="cafeteria-ai-go" onClick={() => askAi(false)} disabled={busy || ask.trim().length < 3}>
                {busy ? "Reading…" : "Find it for me"}
              </Button>
            </div>
            {ai && (
              <div className="mt-4 space-y-3" data-testid="cafeteria-ai-result">
                {ai.note && <p className="border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2 text-sm text-[#0F172A]">{ai.note}</p>}
                {ai.drafts.map((d, i) => (
                  <div key={d.outlet_id} data-testid={`cafeteria-ai-draft-${i}`} className="border border-border bg-slate-50 p-3">
                    <p className="text-sm font-semibold text-[#0F172A]">{d.outlet_name} · {d.symbol}{d.total}</p>
                    <ul className="mt-1 text-sm text-slate-700">
                      {d.lines.map((l, n) => <li key={n}>{l.quantity} × {l.name}</li>)}
                    </ul>
                  </div>
                ))}
                {ai.drafts.length > 0 && (
                  <Button data-testid="cafeteria-ai-place" onClick={() => askAi(true)} disabled={busy}
                    className="w-full sm:w-auto">
                    Place {ai.drafts.length > 1 ? `these ${ai.drafts.length} orders` : "this order"}
                  </Button>
                )}
              </div>
            )}
          </div>

          <div className="scroll-x flex gap-1.5 overflow-x-auto" data-testid="cafeteria-outlets">
            {data.outlets.map((o, i) => {
              const Icon = KIND_ICON[o.kind] || ForkKnife;
              return (
                <button key={o.id} data-testid={`cafeteria-outlet-${i}`} onClick={() => { setOutletId(o.id); setCart({}); }}
                  className={`tap-safe inline-flex shrink-0 items-center gap-1.5 border px-3 py-2 text-xs font-semibold transition-colors duration-150 ${
                    outletId === o.id ? "border-[#0F172A] bg-[#0F172A] text-white"
                      : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                  <Icon size={14} /> {o.name}{!o.open && " · shut"}
                </button>
              );
            })}
          </div>

          {outlet && (
            <div className="border border-border bg-white p-4" data-testid="cafeteria-menu">
              <p className="text-sm font-semibold text-[#0F172A]">{outlet.name}</p>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">{outlet.kind_label}{outlet.note ? ` · ${outlet.note}` : ""}</p>
              {outlet.delivery?.length > 0 && (
                <div className="scroll-x mt-3 flex items-center gap-2 overflow-x-auto" data-testid="cafeteria-delivery">
                  <span className="shrink-0 text-[11px] uppercase tracking-wide text-slate-500">Or get it delivered</span>
                  {outlet.delivery.map((d) => (
                    <a key={d.key} href={d.url} target="_blank" rel="noreferrer"
                      data-testid={`cafeteria-delivery-${d.key}`}
                      className="tap-safe shrink-0 border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]">
                      {d.label}
                    </a>
                  ))}
                </div>
              )}
              {items.length === 0 ? (
                <EmptyState testid="cafeteria-menu-empty" title="Nothing on this menu yet"
                  description="An admin can add items to this outlet's menu." />
              ) : (
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((item, i) => (
                    <div key={item.id} data-testid={`cafeteria-item-${i}`}
                      className={`border border-border ${item.available ? "bg-white" : "bg-slate-50 opacity-60"}`}>
                      {item.image_url && <img src={`${BACKEND}${item.image_url}`} alt={item.name} className="h-32 w-full object-cover" />}
                      <div className="p-3">
                        <p className="font-semibold text-[#0F172A]">{item.name}</p>
                        <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                          {LABEL(item.category)} · {item.symbol}{item.price}{item.available ? "" : " · off today"}
                        </p>
                        {item.dietary.length > 0 && (
                          <p className="mt-1 flex flex-wrap gap-1">
                            {item.dietary.map((d) => (
                              <span key={d} className="border border-border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                {LABEL(d)}
                              </span>
                            ))}
                          </p>
                        )}
                        {item.image_url && (
                          <div className="mt-2">
                            <ShareBar url={`${BACKEND}/api/share/cafeteria/${item.id}`} text={item.name}
                              testid={`cafeteria-share-${i}`} compact instagram />
                          </div>
                        )}
                        <div className="mt-3 flex items-center gap-2">
                          <button data-testid={`cafeteria-minus-${i}`} onClick={() => bump(item.id, -1)} disabled={!item.available}
                            className="tap-safe border border-border p-2 text-slate-600 hover:border-[#0F172A] disabled:opacity-40">
                            <Minus size={13} />
                          </button>
                          <span data-testid={`cafeteria-qty-${i}`} className="min-w-6 text-center text-sm font-semibold">{cart[item.id] || 0}</span>
                          <button data-testid={`cafeteria-plus-${i}`} onClick={() => bump(item.id, 1)} disabled={!item.available}
                            className="tap-safe border border-border p-2 text-slate-600 hover:border-[#0F172A] disabled:opacity-40">
                            <Plus size={13} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {lines.length > 0 && (
            <div className="border border-border bg-white p-4" data-testid="cafeteria-cart">
              <p className="text-sm font-semibold text-[#0F172A]">Your tray — {items[0]?.symbol}{total.toFixed(2)}</p>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {lines.map(([id, q]) => {
                  const item = items.find((i) => i.id === id);
                  return <li key={id}>{q} × {item?.name}</li>;
                })}
              </ul>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div>
                  <Label className="text-xs">How you are paying</Label>
                  <select data-testid="cafeteria-pay" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                    value={pay} onChange={(e) => setPay(e.target.value)}>
                    {data.pay_methods.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Pickup slot</Label>
                  <select data-testid="cafeteria-slot" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
                    value={slot} onChange={(e) => setSlot(e.target.value)}>
                    <option value="">As soon as it is ready</option>
                    {data.slots.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Heads (crew order)</Label>
                  <Input data-testid="cafeteria-heads" type="number" value={crew.heads}
                    onChange={(e) => setCrew({ ...crew, heads: e.target.value })} />
                </div>
              </div>
              <div className="mt-3 space-y-2">
                <label className="tap-safe flex items-center gap-2 text-sm text-slate-600">
                  <input data-testid="cafeteria-for-crew" type="checkbox" className="h-5 w-5 shrink-0"
                    checked={crew.on} onChange={(e) => setCrew({ ...crew, on: e.target.checked })} />
                  This is for the crew, not just me
                </label>
                {(user?.role === "admin" || user?.role === "manager") && (
                  <label className="tap-safe flex items-center gap-2 text-sm text-slate-600">
                    <input data-testid="cafeteria-company-pays" type="checkbox" className="h-5 w-5 shrink-0"
                      checked={crew.company} onChange={(e) => setCrew({ ...crew, company: e.target.checked })} />
                    The company is paying for this one
                  </label>
                )}
              </div>
              <Button className="mt-4 w-full sm:w-auto" data-testid="cafeteria-place-order" onClick={placeOrder} disabled={busy}>
                {busy ? "Sending…" : pay === "stripe" ? "Place order and pay by card" : "Place the order"}
              </Button>
            </div>
          )}
        </>
      )}

      {view === "mine" && (
        orders.length === 0 ? (
          <EmptyState testid="cafeteria-mine-empty" title="No orders yet"
            description="Pick something from an outlet and your order shows up here with its status." />
        ) : (
          <div className="space-y-3" data-testid="cafeteria-my-orders">
            {orders.map((o, i) => (
              <div key={o.id} data-testid={`cafeteria-order-${i}`} className="border border-border bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-[#0F172A]">{o.reference} · {o.outlet_name}</p>
                    <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                      {o.symbol}{o.total} · {o.pay_label} · {o.paid_by === "company" ? "company pays" : "you pay"}
                      {o.slot ? ` · pickup ${o.slot}` : ""}{o.for_crew ? ` · crew of ${o.heads}` : ""}
                    </p>
                  </div>
                  <span className={`border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STATUS_TONE[o.status]}`}
                    data-testid={`cafeteria-order-status-${i}`}>
                    {o.status}{o.eta_minutes != null ? ` · ${o.eta_minutes} min` : ""}
                  </span>
                </div>
                <ul className="mt-2 text-sm text-slate-700">
                  {o.lines.map((l, n) => <li key={n}>{l.quantity} × {l.name}</li>)}
                </ul>
                {o.status === "ready" && (
                  <button data-testid={`cafeteria-collect-${i}`} onClick={() => move(o.id, "collected")}
                    className="tap-safe mt-3 border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0F7A3D] hover:text-[#0B5C2E]">
                    I have collected it
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {view === "board" && board && (
        <div className="space-y-4" data-testid="cafeteria-board">
          <span className="inline-flex items-center gap-1.5 border border-[#0F7A3D] bg-[#EAF7EF] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#0B5C2E]">
            <Broadcast size={13} weight="fill" /> Live · refreshes on its own
          </span>
          {board.popular.length > 0 && (
            <div className="border border-border bg-white p-4" data-testid="cafeteria-popular">
              <p className="text-sm font-semibold text-[#0F172A]">What the floor is eating</p>
              <div className="scroll-x mt-2 flex gap-2 overflow-x-auto">
                {board.popular.map((p) => (
                  <span key={p.name} className="shrink-0 border border-border bg-slate-50 px-2.5 py-1.5 text-xs text-slate-600">
                    {p.name} · {p.count}
                  </span>
                ))}
              </div>
            </div>
          )}
          {board.live.length === 0 ? (
            <EmptyState testid="cafeteria-board-empty" title="Nothing cooking"
              description="Orders show up here the moment someone places one." />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="cafeteria-board-orders">
              {board.live.map((o, i) => (
                <div key={o.id} data-testid={`cafeteria-board-order-${i}`} className="border border-border bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-[#0F172A]">{o.reference} · {o.outlet_name}</p>
                    <span className={`border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${STATUS_TONE[o.status]}`}>
                      {o.status}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                    {o.ordered_by_name}{o.slot ? ` · pickup ${o.slot}` : ""}{o.for_crew ? ` · crew of ${o.heads}` : ""}
                  </p>
                  <ul className="mt-2 text-sm text-slate-700">
                    {o.lines.map((l, n) => <li key={n}>{l.quantity} × {l.name}</li>)}
                  </ul>
                  {board.can_serve && (
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
                      <button data-testid={`cafeteria-preparing-${i}`} onClick={() => move(o.id, "preparing")}
                        className="tap-safe border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#B3241B] hover:text-[#8E1C15]">
                        Cooking
                      </button>
                      <button data-testid={`cafeteria-ready-${i}`} onClick={() => move(o.id, "ready")}
                        className="tap-safe border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0F7A3D] hover:text-[#0B5C2E]">
                        Ready
                      </button>
                      <button data-testid={`cafeteria-collected-${i}`} onClick={() => move(o.id, "collected")}
                        className="tap-safe border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]">
                        Collected
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "vending" && <Vending />}
      {view === "grocery" && <GroceryRun />}

      {view === "credit" && credit && (
        <div className="space-y-4" data-testid="cafeteria-credit">
          <div className="border border-border bg-white p-4">
            <p className="text-sm font-semibold text-[#0F172A]">Your meal credit</p>
            <p className="mt-1 text-2xl font-bold text-[#0B5C2E]" data-testid="cafeteria-credit-balance">
              {credit.currency} {credit.balance.toFixed(2)}
            </p>
            <p className="mt-2 text-xs text-slate-600">
              Coming off this month's wage: {credit.currency} {(credit.payroll_total || 0).toFixed(2)}
            </p>
            {credit.payroll.length > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-slate-600" data-testid="cafeteria-payroll-lines">
                {credit.payroll.map((p) => <li key={p.reference}>{p.reference} · {p.currency} {p.amount}</li>)}
              </ul>
            )}
          </div>
          {user?.role === "admin" && <CreditAdmin credit={credit} currencies={data.currencies} onDone={() => {
            api.get("/cafeteria/credit").then(({ data: d }) => setCredit(d));
          }} />}
        </div>
      )}
    </div>
  );
}

const CreditAdmin = ({ credit, currencies, onDone }) => {
  const [form, setForm] = useState({ user_id: "", amount: "", currency: "usd" });
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/members").then(({ data }) => setPeople(data)).catch(() => setPeople([]));
  }, []);

  const topUp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cafeteria/credit/top-up", {
        user_id: form.user_id, amount: Number(form.amount), currency: form.currency });
      toast.success(`Topped up — ${data.currency} ${data.balance}`);
      setForm({ ...form, amount: "" });
      onDone();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  const settle = async (userId) => {
    try {
      const { data } = await api.post(`/cafeteria/payroll/settle?user_id=${userId}`);
      toast.success(`${data.settled} deduction(s) settled`);
      onDone();
    } catch (e) { oops(e); }
  };

  return (
    <div className="border border-border bg-white p-4" data-testid="cafeteria-credit-admin">
      <p className="text-sm font-semibold text-[#0F172A]">Top up someone's credit</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <div>
          <Label className="text-xs">Who</Label>
          <select data-testid="cafeteria-topup-user" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
            value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })}>
            <option value="">Pick someone</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.role}</option>)}
          </select>
        </div>
        <div><Label className="text-xs">Amount</Label>
          <Input data-testid="cafeteria-topup-amount" type="number" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
        <div>
          <Label className="text-xs">Currency</Label>
          <select data-testid="cafeteria-topup-currency" className="mt-1 w-full border border-border bg-white px-2 py-2 text-base md:text-sm"
            value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}>
            {currencies.map((c) => <option key={c.code} value={c.code.toLowerCase()}>{c.code} {c.symbol}</option>)}
          </select>
        </div>
      </div>
      <Button className="mt-3 w-full sm:w-auto" data-testid="cafeteria-topup-save" onClick={topUp}
        disabled={busy || !form.user_id || !(Number(form.amount) > 0)}>
        {busy ? "Topping up…" : "Add the credit"}
      </Button>

      {(credit.payroll_owed || []).length > 0 && (
        <div className="mt-5 border-t border-border pt-4" data-testid="cafeteria-payroll-owed">
          <p className="text-sm font-semibold text-[#0F172A]">Coming off wages this month</p>
          <ul className="mt-2 space-y-2">
            {credit.payroll_owed.map((row) => (
              <li key={row.user_id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-700">
                <span>{row.user_name} · {row.amount.toFixed(2)}</span>
                <button onClick={() => settle(row.user_id)} data-testid={`cafeteria-settle-${row.user_id}`}
                  className="tap-safe border border-border px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-[#0F7A3D] hover:text-[#0B5C2E]">
                  Settled
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
